import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Users, UserPlus, Calendar, Clock, Shield, AlertTriangle,
  FileText, Briefcase, RefreshCw, Building2, ChevronRight,
  CheckCircle2, UserCheck, ClipboardList
} from 'lucide-react';

function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default', link }) {
  const variants = {
    default: 'bg-card hover:border-primary/30',
    primary: 'bg-primary/5 border-primary/20 hover:border-primary/40',
    success: 'bg-green-500/5 border-green-500/20 hover:border-green-500/40',
    warning: 'bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40',
    danger: 'bg-red-500/5 border-red-500/20 hover:border-red-500/40',
  };

  const content = (
    <Card className={`${variants[variant]} transition-colors ${link ? 'cursor-pointer' : ''}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {Icon && (
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                variant === 'danger' ? 'bg-red-500/10' :
                variant === 'warning' ? 'bg-amber-500/10' :
                variant === 'success' ? 'bg-green-500/10' :
                'bg-primary/10'
              }`}>
                <Icon className={`h-5 w-5 ${
                  variant === 'danger' ? 'text-red-500' :
                  variant === 'warning' ? 'text-amber-500' :
                  variant === 'success' ? 'text-green-500' :
                  'text-primary'
                }`} />
              </div>
            )}
            {link && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
}

function DetailCard({ title, items }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex justify-between items-center py-1.5">
            <div className="flex items-center gap-2">
              {item.icon && <item.icon className={`h-4 w-4 ${item.iconColor || 'text-muted-foreground'}`} />}
              <span className="text-sm">{item.label}</span>
            </div>
            <span className={`font-medium ${item.valueColor || ''}`}>{item.value}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function PipelineCard({ stages }) {
  if (!stages || Object.keys(stages).length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Candidate Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No candidates in pipeline</p>
        </CardContent>
      </Card>
    );
  }
  
  const stageColors = {
    APPLIED: 'bg-slate-500',
    SCREENING: 'bg-blue-500',
    INTERVIEW: 'bg-purple-500',
    ASSESSMENT: 'bg-indigo-500',
    REFERENCE_CHECK: 'bg-cyan-500',
    OFFER: 'bg-amber-500',
  };
  
  const total = Object.values(stages).reduce((sum, count) => sum + count, 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Candidate Pipeline</CardTitle>
        <CardDescription>{total} total candidates</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(stages).map(([stage, count]) => {
          const percent = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={stage} className="space-y-1">
              <div className="flex justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${stageColors[stage] || 'bg-slate-400'}`} />
                  <span>{stage.replace(/_/g, ' ')}</span>
                </div>
                <span className="font-medium">{count}</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full ${stageColors[stage] || 'bg-slate-400'}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default function HRDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branchId') || '');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedBranch ? { branchId: selectedBranch } : {};
      const [kpiRes, branchRes] = await Promise.all([
        api.get('/kpi/hr', { params }),
        api.get('/org/branches'),
      ]);
      setKpis(kpiRes.data.data);
      setBranches(branchRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load HR data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch]);

  const handleBranchChange = (value) => {
    setSelectedBranch(value === 'all' ? '' : value);
    if (value && value !== 'all') {
      setSearchParams({ branchId: value });
    } else {
      setSearchParams({});
    }
  };

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const details = kpis?.details || {};

  // Calculate alert count
  const alertCount = (kpis?.onboardingTasksOverdue || 0) + 
                     (details.compliance?.expiring7Days || 0) + 
                     (details.compliance?.expired || 0);

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">HR Dashboard</h1>
          <p className="text-muted-foreground">Recruitment, onboarding, and compliance</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedBranch || 'all'} onValueChange={handleBranchChange}>
            <SelectTrigger className="w-[200px]" data-testid="branch-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {alertCount > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div className="flex-1">
                <p className="font-medium">HR Alerts</p>
                <p className="text-sm text-muted-foreground">
                  {kpis?.onboardingTasksOverdue > 0 && (
                    <span className="text-red-600">{kpis.onboardingTasksOverdue} overdue onboarding tasks</span>
                  )}
                  {kpis?.onboardingTasksOverdue > 0 && details.compliance?.expired > 0 && ' · '}
                  {details.compliance?.expired > 0 && (
                    <span className="text-red-600">{details.compliance.expired} expired compliance items</span>
                  )}
                  {(kpis?.onboardingTasksOverdue > 0 || details.compliance?.expired > 0) && details.compliance?.expiring7Days > 0 && ' · '}
                  {details.compliance?.expiring7Days > 0 && (
                    <span className="text-amber-600">{details.compliance.expiring7Days} expiring in 7 days</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary KPIs - Row 1: Recruitment */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Recruitment</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            title="Candidates in Pipeline"
            value={kpis?.candidatesInPipeline || 0}
            subtitle={`${details.candidates?.newThisWeek || 0} new this week · ${details.candidates?.openPositions || 0} open positions`}
            icon={Users}
            variant="primary"
            link="/hr/recruitment"
          />
          <KpiCard
            title="Upcoming Interviews"
            value={kpis?.upcomingInterviews || 0}
            subtitle={`${details.interviews?.today || 0} today`}
            icon={Calendar}
            variant={kpis?.upcomingInterviews > 0 ? 'primary' : 'default'}
          />
          <KpiCard
            title="Offers Pending"
            value={kpis?.offersPending || 0}
            subtitle={details.offers?.expiringSoon > 0 ? `${details.offers.expiringSoon} expiring soon` : 'Awaiting response'}
            icon={FileText}
            variant={details.offers?.expiringSoon > 0 ? 'warning' : 'default'}
          />
        </div>
      </div>

      {/* Primary KPIs - Row 2: Onboarding & Compliance */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Onboarding & Compliance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Onboarding Tasks Overdue"
            value={kpis?.onboardingTasksOverdue || 0}
            subtitle={`${details.onboarding?.pending || 0} total pending · ${details.onboarding?.dueThisWeek || 0} due this week`}
            icon={ClipboardList}
            variant={kpis?.onboardingTasksOverdue > 0 ? 'danger' : 'success'}
            link="/hr/onboarding"
          />
          <KpiCard
            title="Staff Expiring Compliance"
            value={kpis?.staffExpiringCompliance || 0}
            subtitle={`${details.compliance?.expiring7Days || 0} urgent (7 days) · ${details.compliance?.expired || 0} expired`}
            icon={Shield}
            variant={kpis?.staffExpiringCompliance > 10 ? 'danger' : kpis?.staffExpiringCompliance > 5 ? 'warning' : 'success'}
            link="/compliance/staff"
          />
          <KpiCard
            title="Probation Ending Soon"
            value={kpis?.probationEndingSoon || 0}
            subtitle={`${details.probation?.endingThisWeek || 0} this week`}
            icon={UserCheck}
            variant={kpis?.probationEndingSoon > 0 ? 'warning' : 'default'}
          />
          <KpiCard
            title="Staff Hours This Week"
            value={`${kpis?.staffHoursThisWeek || 0}h`}
            subtitle={`${details.staffHours?.staffWorking || 0} staff working`}
            icon={Clock}
            variant="primary"
          />
        </div>
      </div>

      {/* Detail Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Candidate Pipeline */}
        <PipelineCard stages={details.candidates?.byStage} />

        {/* Interview Details */}
        <DetailCard
          title="Interview Schedule"
          items={[
            { label: 'Upcoming (7 days)', value: kpis?.upcomingInterviews || 0, icon: Calendar },
            { label: 'Today', value: details.interviews?.today || 0, icon: Clock, iconColor: 'text-primary', valueColor: details.interviews?.today > 0 ? 'text-primary font-bold' : '' },
            ...Object.entries(details.interviews?.byType || {}).map(([type, count]) => ({
              label: type,
              value: count
            }))
          ]}
        />

        {/* Offers Details */}
        <DetailCard
          title="Offers Summary"
          items={[
            { label: 'Pending Response', value: kpis?.offersPending || 0, icon: FileText },
            { label: 'Expiring Soon', value: details.offers?.expiringSoon || 0, icon: AlertTriangle, iconColor: 'text-amber-500', valueColor: details.offers?.expiringSoon > 0 ? 'text-amber-600' : '' },
            ...Object.entries(details.offers?.byStatus || {}).map(([status, count]) => ({
              label: status,
              value: count,
              icon: status === 'ACCEPTED' ? CheckCircle2 : undefined,
              iconColor: status === 'ACCEPTED' ? 'text-green-500' : undefined
            }))
          ]}
        />

        {/* Compliance Details */}
        <DetailCard
          title="Compliance Status"
          items={[
            { label: 'Expiring in 30 Days', value: kpis?.staffExpiringCompliance || 0, icon: Shield, iconColor: 'text-amber-500' },
            { label: 'Expiring in 7 Days', value: details.compliance?.expiring7Days || 0, icon: AlertTriangle, iconColor: 'text-red-500', valueColor: details.compliance?.expiring7Days > 0 ? 'text-red-600 font-bold' : '' },
            { label: 'Already Expired', value: details.compliance?.expired || 0, icon: AlertTriangle, iconColor: 'text-red-500', valueColor: details.compliance?.expired > 0 ? 'text-red-600 font-bold' : '' },
            ...Object.entries(details.compliance?.byType || {}).slice(0, 5).map(([type, count]) => ({
              label: type,
              value: count
            }))
          ]}
        />

        {/* Onboarding Details */}
        <DetailCard
          title="Onboarding Tasks"
          items={[
            { label: 'Overdue', value: kpis?.onboardingTasksOverdue || 0, icon: AlertTriangle, iconColor: 'text-red-500', valueColor: kpis?.onboardingTasksOverdue > 0 ? 'text-red-600 font-bold' : '' },
            { label: 'Due This Week', value: details.onboarding?.dueThisWeek || 0, icon: Calendar },
            { label: 'Total Pending', value: details.onboarding?.pending || 0, icon: ClipboardList },
          ]}
        />

        {/* Staff Hours */}
        <DetailCard
          title="Staff Hours"
          items={[
            { label: 'Hours This Week', value: `${kpis?.staffHoursThisWeek || 0}h`, icon: Clock },
            { label: 'Staff Working', value: details.staffHours?.staffWorking || 0, icon: Users },
            { label: 'Probation Ending (30 days)', value: kpis?.probationEndingSoon || 0, icon: UserCheck },
            { label: 'Probation Ending This Week', value: details.probation?.endingThisWeek || 0, icon: AlertTriangle, iconColor: 'text-amber-500' },
          ]}
        />
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" asChild>
          <Link to="/hr/recruitment">Recruitment</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/hr/onboarding">Onboarding</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/compliance/staff">Staff Compliance</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/staff/list">Staff Profiles</Link>
        </Button>
      </div>

      {/* Data Sources & Period */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">Data Sources:</span> candidates, interviews, offers, onboarding tasks, compliance, payroll line items
            </div>
            <div>
              Week: {kpis?.period?.weekStart} to {kpis?.period?.weekEnd} · 
              Generated: {kpis?.generatedAt ? new Date(kpis.generatedAt).toLocaleString() : 'N/A'}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
